import { StateContext } from '@ngxs/store';
import { concat, defer, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, ignoreElements, map } from 'rxjs/operators';

import { defaultRequestState, IRequestsNestedState } from './interfaces/requests.interface';

// Where a state keeps the IRequestsNestedState for this request: either a field
// on the state model, or - for states that group their request states under a
// nested object - a function folding the new request state into a patch.
export type RequestStateTarget<S> =
  | keyof S
  | ((requestState: IRequestsNestedState, state: S) => Partial<S>);

interface SettledRequest<T> {
  failed: boolean;
  payload: T;
}

/**
 * Runs `request`, dispatches the matching success/fail action, and keeps the
 * request state (`loading`/`loaded`/`status`/`data`) in sync.
 *
 * This bookkeeping used to come from `ngxs-requests-plugin`, which was dropped
 * in 9eb275b/68e2807 along with the state argument identifying the request
 * state to write; handlers have been hand-rolling it back since 5f55a3e.
 * `requestStateTarget` is optional because several states track no request
 * state at all.
 */
export function dispatchRequest<T, S = any>(
  request: Observable<T>,
  ctx: StateContext<S>,
  successAction: new (payload: T) => any,
  failAction?: new (error: any) => any,
  requestStateTarget?: RequestStateTarget<S>,
): Observable<T> {
  const patchRequestState = (requestState: IRequestsNestedState) => {
    if (requestStateTarget === undefined || requestStateTarget === null) {
      return;
    }

    ctx.patchState(
      typeof requestStateTarget === 'function'
        ? requestStateTarget(requestState, ctx.getState())
        : ({ [requestStateTarget]: requestState } as unknown as Partial<S>),
    );
  };

  // Settles only once the dispatched action has been fully handled. Consumers
  // gate on `newRequestFinished()` + status === 'success', so settling first
  // would let them read the store before the record they are waiting for has
  // landed - e.g. a modal dismissing before its save is in the store.
  const settleAfter = (handled: Observable<any>, requestState: IRequestsNestedState, result: T) =>
    concat(
      handled.pipe(
        ignoreElements(),
        // A handler that errors would otherwise skip the settle below and leave
        // the request in flight for good - and several consumers pipe `loading`
        // into the global app spinner, so that reads as a permanent
        // full-screen overlay. The request itself did succeed, so no fail
        // action is dispatched, but the state must not settle as `success`
        // either: consumers gate on that and would read a store the handler
        // never finished writing.
        catchError((error) => {
          patchRequestState({
            loading: false,
            loaded: true,
            status: 'error',
            data: error,
          });
          return throwError(() => error);
        }),
      ),
      defer(() => {
        patchRequestState(requestState);
        return of(result);
      }),
    );

  patchRequestState({ ...defaultRequestState, loading: true });

  return request.pipe(
    map((response: T): SettledRequest<T> => ({ failed: false, payload: response })),
    // only a failure of `request` itself takes the fail path - an error raised
    // by the success handler must not be reported back as a failed request
    catchError((error) => of<SettledRequest<T>>({ failed: true, payload: error })),
    concatMap(({ failed, payload }) => {
      if (!failed) {
        return settleAfter(
          ctx.dispatch(new successAction(payload)),
          { loading: false, loaded: true, status: 'success', data: payload },
          payload,
        );
      }

      console.error('Request failed:', payload);
      const errorState: IRequestsNestedState = {
        loading: false,
        loaded: true,
        status: 'error',
        data: payload,
      };

      // several callers pass no fail action; `new undefined(...)` would throw
      // here and swallow the original failure
      if (!failAction) {
        patchRequestState(errorState);
        return of(payload);
      }

      return settleAfter(ctx.dispatch(new failAction(payload)), errorState, payload);
    }),
  );
}
